"use client";

import { motion } from "framer-motion";

import { CrtChassis } from "./retro-computer";
import {
  ClickRing,
  MirroredMouse,
  ScreenCursor,
  TypeLine,
  useArtScript,
} from "./screen-bits";

/**
 * The /desktop hero artifact: a chunky CRT terminal types out a capture
 * session while the keyboard's keys physically press along. The upload bar
 * fills, a verified payout lands in green phosphor, and the desk mouse wakes
 * up to sweep a selection over it. Then the screen clears and the session
 * loops. Screen is 320x200; coordinates below are screen pixels.
 */

const LINES = [
  { id: "ln-0", text: "$ terac record start", tone: "cream" },
  { id: "ln-1", text: "● screen capture · 60 fps", tone: "muted" },
  { id: "ln-2", text: "● input events · mouse + keys", tone: "muted" },
  { id: "ln-3", text: "▸ task: reconcile Q2 invoices", tone: "cream" },
] as const;

const PAYOUT_LINE = "✓ verified · +$78.00 queued";

/** idle prompt, 4 typed lines, upload, payout, sweep, copied, clear */
const DURATIONS = [
  900, 1100, 1000, 1100, 1200, 1600, 1100, 1700, 1000, 500,
] as const;

const LINE_X = 18;
const LINE_Y0 = 14;
const LINE_H = 22;
const PAYOUT_Y = LINE_Y0 + 5 * LINE_H;

/** Cursor parking spot (screen center) keeps the desk mouse still while typing. */
const CURSOR_IDLE = { x: 160, y: 100 } as const;
const CURSOR_SWEEP_START = { x: 14, y: PAYOUT_Y + 9 } as const;
const CURSOR_SWEEP_END = { x: 224, y: PAYOUT_Y + 9 } as const;

export default function TerminalArt() {
  const { i, loop } = useArtScript(DURATIONS, 8);

  const idle = i === 0;
  const cleared = i === 9;
  const uploading = i === 5;
  const payoutVisible = i >= 6;
  const sweeping = i === 7;
  const copied = i === 8;
  const cursorVisible = sweeping || copied;
  const typingActive = (i >= 1 && i <= 4) || i === 6;

  const lineMode = (k: number): "hidden" | "typing" | "done" => {
    if (cleared) {
      return "hidden";
    }
    if (i === k + 1) {
      return "typing";
    }
    return i > k + 1 ? "done" : "hidden";
  };

  // Pre-position at the line start while still hidden (step 6) so the visible
  // travel during the sweep runs left to right with the highlight.
  const cursor =
    sweeping || copied
      ? CURSOR_SWEEP_END
      : i === 6
        ? CURSOR_SWEEP_START
        : CURSOR_IDLE;

  return (
    <div className="relative w-[380px]">
      <CrtChassis led="on" keyboardActive={typingActive}>
        {/* Phosphor glow on everything the tube draws */}
        <div
          className="absolute inset-0"
          style={{ textShadow: "0 0 8px rgba(255, 255, 240, 0.3)" }}
        >
          {idle && !cleared ? (
            <p
              className="text-marketing-cream absolute font-mono text-[11px]"
              style={{ left: LINE_X, top: LINE_Y0 }}
            >
              $ <span className="animate-pulse">▌</span>
            </p>
          ) : null}

          {!cleared ? (
            <div
              className="absolute"
              style={{ left: LINE_X, top: LINE_Y0, right: LINE_X }}
            >
              {LINES.map((line, k) => (
                <TypeLine
                  key={`${loop}-${line.id}`}
                  text={line.text}
                  mode={lineMode(k)}
                  className={
                    line.tone === "cream"
                      ? "text-marketing-cream"
                      : "text-marketing-cream/60"
                  }
                />
              ))}

              {/* Upload bar row */}
              {i >= 5 ? (
                <div className="flex h-[22px] items-center gap-2.5">
                  <span className="border-marketing-cream/40 h-[8px] w-[150px] overflow-hidden rounded-full border">
                    <motion.span
                      key={`${loop}-bar`}
                      className="bg-marketing-cream block h-full rounded-full"
                      initial={{ width: "5%" }}
                      animate={{ width: "100%" }}
                      transition={{ duration: 1.4, ease: "easeInOut" }}
                    />
                  </span>
                  <span className="text-marketing-cream/60 font-mono text-[11px]">
                    {uploading ? "uploading…" : "uploaded ✓"}
                  </span>
                </div>
              ) : (
                <div className="h-[22px]" />
              )}

              {payoutVisible ? (
                <TypeLine
                  key={`${loop}-payout`}
                  text={PAYOUT_LINE}
                  mode={i === 6 ? "typing" : "done"}
                  className="text-marketing-accent-green"
                />
              ) : null}
            </div>
          ) : null}
        </div>

        {/* Selection sweep over the payout line */}
        {sweeping || copied ? (
          <motion.span
            key={`${loop}-sweep`}
            className="bg-marketing-blue/40 absolute z-10 h-[18px] rounded-[3px]"
            style={{ left: LINE_X - 3, top: PAYOUT_Y + 1 }}
            initial={{ width: 0 }}
            animate={{ width: 206 }}
            transition={{ duration: 0.8, delay: 0.2, ease: "easeOut" }}
          />
        ) : null}

        {/* The little reward beat after the sweep */}
        {copied ? (
          <motion.span
            key={`${loop}-copied`}
            className="bg-marketing-cream text-marketing-navy absolute z-30 rounded-[5px] px-1.5 py-[2px] font-mono text-[9px]"
            style={{ left: 196, top: PAYOUT_Y - 22 }}
            initial={{ opacity: 0, transform: "translateY(4px) scale(0.5)" }}
            animate={{ opacity: 1, transform: "translateY(0px) scale(1)" }}
            transition={{ type: "spring", stiffness: 320, damping: 20 }}
          >
            copied
          </motion.span>
        ) : null}

        {sweeping ? (
          <ClickRing
            key={`${loop}-ring`}
            x={CURSOR_SWEEP_START.x + 4}
            y={CURSOR_SWEEP_START.y}
            tone="cream"
            delay={0.05}
          />
        ) : null}
        <ScreenCursor
          x={cursor.x}
          y={cursor.y}
          pressed={sweeping}
          hidden={!cursorVisible}
        />
      </CrtChassis>

      {/* Desk mouse, parked beside the keyboard; wakes up for the sweep */}
      <MirroredMouse
        x={cursor.x}
        y={cursor.y}
        className="absolute -right-[88px] bottom-[10px]"
      />
    </div>
  );
}
